import { z } from 'zod';

/**
 * Contracten van de peilingen, de moderatie en de @-vermeldingen (Stap 26).
 *
 * Apart bestand naast `index.ts`, want dat bestand hoort bij de eerste slice (Stap 12b) en is
 * bewust klein gebleven. Hier komt het stuk bij dat een peiling een peiling maakt: de keuze tussen
 * anoniem en op naam, en wie de uitslag wanneer ziet.
 *
 * De belangrijkste regel in dit bestand staat niet in een veld maar in wat er **niet** is: er is
 * geen enkel antwoord waarin een persoon aan een optie gekoppeld wordt bij een anonieme peiling.
 * Niet verborgen achter een recht — er bestaat geen rij om op te vragen (ADR-0058).
 */

/**
 * Anoniem of op naam, en de medewerker ziet het vóór hij kiest.
 *
 * Geen standaardwaarde in dit schema: de opsteller moet kiezen. Een peiling waarvan de modus per
 * ongeluk "op naam" is, is precies de fout die niemand opmerkt tot iemand de uitslag opvraagt.
 */
export const pollModeSchema = z.enum(['anonymous', 'named']);
export type PollMode = z.infer<typeof pollModeSchema>;

/**
 * Wie de uitslag wanneer ziet.
 *
 * `never` betekent: alleen de opsteller. Bij een gevoelige vraag ("voel je je veilig in hal 3")
 * verandert een zichtbare tussenstand het antwoord van wie later stemt.
 */
export const pollResultsVisibilitySchema = z.enum(['never', 'after_vote', 'after_close']);
export type PollResultsVisibility = z.infer<typeof pollResultsVisibilitySchema>;

/** Een optie is tekst of een datum. De soort bepaalt alleen hoe de client hem toont. */
export const pollOptionKindSchema = z.enum(['text', 'date']);
export type PollOptionKind = z.infer<typeof pollOptionKindSchema>;

export const pollOptionSchema = z.object({
  id: z.guid(),
  label: z.string(),
  kind: pollOptionKindSchema,
  /**
   * Het aantal stemmen, of leeg als de aanvrager de uitslag nog niet mag zien.
   *
   * `null` en niet `0`: "nul stemmen" en "je mag het niet weten" zijn twee verschillende dingen,
   * en een client die ze niet kan onderscheiden toont een staafdiagram van nullen (ADR-0049).
   */
  voteCount: z.number().int().nullable(),
  /** Of ík deze optie koos. Bij een anonieme peiling altijd `null`: de server weet het niet. */
  chosenByMe: z.boolean().nullable(),
});
export type PollOption = z.infer<typeof pollOptionSchema>;

export const pollSchema = z.object({
  id: z.guid(),
  postId: z.guid(),
  question: z.string(),
  mode: pollModeSchema,
  /** Meerdere opties aanvinken of precies één. */
  multi: z.boolean(),
  resultsVisibility: pollResultsVisibilitySchema,
  closesAt: z.string().datetime().nullable(),
  /** Afgeleid van `closesAt` en de klok van de server, zodat de client niet hoeft te rekenen. */
  closed: z.boolean(),
  options: z.array(pollOptionSchema),
  /**
   * Of een medewerker zelf een optie mag toevoegen (Extra 6, punt 5).
   *
   * Voor een vraag waarvan de opsteller de antwoorden niet kent: "welk gereedschap mis je op
   * lijn 3". De kolom bestond al sinds Stap 26; er was alleen nooit een weg naartoe.
   */
  allowCustom: z.boolean(),
  /** Heb ík gestemd? Dit weet de server ook bij een anonieme peiling — maar niet wát ik koos. */
  votedByMe: z.boolean(),
  /** Hoeveel mensen er gestemd hebben. Een aantal, nooit namen (ADR-0049). */
  voterCount: z.number().int(),
  /** Mag ik de uitslag nu zien? Zo niet, dan staat er in `voteCount` overal `null`. */
  resultsVisible: z.boolean(),
  /**
   * Waarom de uitslag er niet is, als die er niet is. De medewerker hoort te horen wát eraan
   * scheelt: "nog niet gestemd" is een ander gesprek dan "te weinig stemmen".
   */
  resultsWithheldReason: z
    .enum(['not_voted_yet', 'not_closed_yet', 'never', 'too_few_voters'])
    .nullable(),
});
export type Poll = z.infer<typeof pollSchema>;

export const createPollOptionSchema = z.object({
  label: z.string().trim().min(1, 'Een optie mag niet leeg zijn.').max(120),
  kind: pollOptionKindSchema.default('text'),
});
export type CreatePollOption = z.infer<typeof createPollOptionSchema>;

export const createPollRequestSchema = z.object({
  question: z.string().trim().min(1, 'Stel een vraag.').max(300),
  mode: pollModeSchema,
  multi: z.boolean().default(false),
  resultsVisibility: pollResultsVisibilitySchema.default('after_vote'),
  closesAt: z.string().datetime().nullable().default(null),
  /** Mogen medewerkers zelf een optie toevoegen? Standaard niet (Extra 6, punt 5). */
  allowCustom: z.boolean().default(false),
  options: z
    .array(createPollOptionSchema)
    .min(2, 'Een peiling heeft minstens twee opties.')
    .max(10, 'Tien opties is het maximum; meer wordt op een telefoon een lijst om te scrollen.'),
});
export type CreatePollRequest = z.infer<typeof createPollRequestSchema>;

/**
 * Een eigen optie toevoegen aan een open peiling (Extra 6, punt 5).
 *
 * Zonder `kind`: wie zelf iets invult, typt tekst. Een datumoptie is een keuze van de opsteller,
 * die weet waarover de vraag gaat.
 */
export const addPollOptionRequestSchema = z.object({
  label: z.string().trim().min(1, 'Schrijf iets.').max(120),
});
export type AddPollOptionRequest = z.infer<typeof addPollOptionRequestSchema>;

export const votePollRequestSchema = z.object({
  /** Eén id bij een enkele keuze, meerdere bij `multi`. Leeg is geen stem maar een fout. */
  optionIds: z.array(z.guid()).min(1, 'Kies minstens één optie.').max(10),
});
export type VotePollRequest = z.infer<typeof votePollRequestSchema>;

/**
 * De uitslag van een peiling **op naam**, met wie wat koos.
 *
 * Bestaat niet voor een anonieme peiling, en dat is geen filter maar een feit: de rijen zijn er
 * niet (ADR-0058). Achter `interaction.poll.names.view`, een recht dat in geen enkele systeemrol
 * zit — dezelfde lijn als `reach.names.view` (ADR-0014).
 */
export const pollVoterSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  optionIds: z.array(z.guid()),
});
export type PollVoter = z.infer<typeof pollVoterSchema>;

export const pollVoterListSchema = z.object({
  pollId: z.guid(),
  items: z.array(pollVoterSchema),
});
export type PollVoterList = z.infer<typeof pollVoterListSchema>;

// --- Reacties aan of uit, en moderatie -----------------------------------------------

export const commentSettingSchema = z.object({
  postId: z.guid(),
  enabled: z.boolean(),
});
export type CommentSetting = z.infer<typeof commentSettingSchema>;

export const updateCommentSettingRequestSchema = z.object({ enabled: z.boolean() });
export type UpdateCommentSettingRequest = z.infer<typeof updateCommentSettingRequestSchema>;

export const reportCommentRequestSchema = z.object({
  reason: z.string().trim().max(500).default(''),
});
export type ReportCommentRequest = z.infer<typeof reportCommentRequestSchema>;

/**
 * Eén gemelde reactie zoals een moderator hem ziet.
 *
 * Mét de tekst en mét de naam van de auteur, en dat is hier onvermijdelijk: beoordelen of een
 * reactie weg moet, kan niet zonder te lezen wat er staat. Daarom zit het achter een eigen recht
 * en niet achter `communication.post.draft`.
 */
export const commentReportSchema = z.object({
  commentId: z.guid(),
  postId: z.guid(),
  postTitle: z.string(),
  authorName: z.string(),
  body: z.string(),
  commentCreatedAt: z.string().datetime(),
  /** Hoeveel mensen deze reactie meldden. Namen van melders komen er nooit uit. */
  reportCount: z.number().int(),
  /** De opgegeven redenen, zonder wie ze opgaf. */
  reasons: z.array(z.string()),
  lastReportedAt: z.string().datetime(),
  /** Al weggehaald door de auteur of een moderator; de melding blijft in de lijst staan. */
  deleted: z.boolean(),
});
export type CommentReport = z.infer<typeof commentReportSchema>;

export const commentReportListSchema = z.object({ items: z.array(commentReportSchema) });
export type CommentReportList = z.infer<typeof commentReportListSchema>;

/** Iemand om te @-vermelden: alleen mensen die het bericht zelf ook kregen. */
export const mentionCandidateSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
});
export type MentionCandidate = z.infer<typeof mentionCandidateSchema>;

export const mentionCandidateListSchema = z.object({ items: z.array(mentionCandidateSchema) });
export type MentionCandidateList = z.infer<typeof mentionCandidateListSchema>;
